import { useState, useCallback } from 'react';
import { Settings as SettingsIcon, Plus, X, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAccountStore } from '../../stores/accountStore';
import { usePolyAccountStore } from '../../stores/polyAccountStore';
import type { KalshiKey, KalshiCredentials } from '../../types/kalshi';
import { SoundSettingsPanel } from './SoundSettingsPanel';

// ── Settings Screen ─────────────────────────────────────────────

export function Settings() {
  const status = useAccountStore((s) => s.status);
  const error = useAccountStore((s) => s.error);
  const credentials = useAccountStore((s) => s.credentials);
  const balance = useAccountStore((s) => s.balance);
  const keyHealth = useAccountStore((s) => s.keyHealth);
  const connect = useAccountStore((s) => s.connect);
  const disconnect = useAccountStore((s) => s.disconnect);
  const removeCredentials = useAccountStore((s) => s.removeCredentials);

  // Local state for key management
  const [environment, setEnvironment] = useState<'prod' | 'demo'>(
    credentials?.environment ?? 'prod',
  );
  const [keys, setKeys] = useState<KalshiKey[]>(credentials?.keys ?? []);
  const [showAddForm, setShowAddForm] = useState(false);

  // Sync local state when credentials change from auto-connect
  const credKeys = credentials?.keys;
  const credEnv = credentials?.environment;
  useState(() => {
    if (credKeys?.length) setKeys(credKeys);
    if (credEnv) setEnvironment(credEnv);
  });

  const handleAddKey = useCallback((key: KalshiKey) => {
    setKeys((prev) => [...prev, key]);
    setShowAddForm(false);
  }, []);

  const handleRemoveKey = useCallback((keyId: string) => {
    setKeys((prev) => prev.filter((k) => k.id !== keyId));
  }, []);

  const handleConnect = useCallback(() => {
    if (keys.length === 0) return;
    const creds: KalshiCredentials = { environment, keys };
    connect(creds);
  }, [keys, environment, connect]);

  const handleDisconnect = useCallback(() => {
    disconnect();
  }, [disconnect]);

  const handleRemoveCredentials = useCallback(() => {
    removeCredentials();
    setKeys([]);
  }, [removeCredentials]);

  const isConnected = status === 'connected';
  const isConnecting = status === 'connecting';

  return (
    <div className="h-full overflow-auto p-6">
      <div className="mx-auto max-w-2xl space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <SettingsIcon size={24} className="text-gray-400" />
          <h1 className="text-xl font-semibold">Settings</h1>
        </div>

        {/* Kalshi Account Card */}
        <div className="rounded-lg border border-gray-800 bg-gray-900/50 p-5">
          {/* Card Header */}
          <div className="mb-5 flex items-center justify-between">
            <h2 className="text-base font-medium">Kalshi Account</h2>
            <StatusBadge status={status} balance={balance} />
          </div>

          {/* Environment Toggle */}
          <div className="mb-5">
            <label className="mb-1.5 block text-xs font-medium text-gray-400">Environment</label>
            <div className="flex gap-2">
              {(['prod', 'demo'] as const).map((env) => (
                <button
                  key={env}
                  onClick={() => setEnvironment(env)}
                  disabled={isConnected}
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    environment === env
                      ? 'bg-blue-500/20 text-blue-400 ring-1 ring-blue-500/40'
                      : 'bg-gray-800 text-gray-400 hover:text-gray-300'
                  } disabled:opacity-50`}
                >
                  {env === 'prod' ? 'Production' : 'Demo'}
                </button>
              ))}
            </div>
          </div>

          {/* Key List */}
          <div className="mb-4">
            <label className="mb-1.5 block text-xs font-medium text-gray-400">API Keys</label>
            {keys.length > 0 ? (
              <div className="space-y-1.5">
                {keys.map((key) => {
                  const health = keyHealth.find((h) => h.keyId === key.id);
                  return (
                    <KeyRow
                      key={key.id}
                      keyData={key}
                      health={health}
                      canRemove={keys.length > 1}
                      disabled={isConnected}
                      onRemove={() => handleRemoveKey(key.id)}
                    />
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-gray-500">No keys added yet.</p>
            )}
          </div>

          {/* Add Key */}
          {!isConnected && (
            <>
              {showAddForm ? (
                <AddKeyForm onAdd={handleAddKey} onCancel={() => setShowAddForm(false)} />
              ) : (
                <button
                  onClick={() => setShowAddForm(true)}
                  className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs text-blue-400 transition-colors hover:bg-blue-500/10"
                >
                  <Plus size={14} />
                  Add Key
                </button>
              )}
            </>
          )}

          {/* Error Display */}
          {error && (
            <div className="mt-4 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-5 flex items-center gap-3 border-t border-gray-800 pt-4">
            {!isConnected ? (
              <button
                onClick={handleConnect}
                disabled={keys.length === 0 || isConnecting}
                className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-500 disabled:opacity-50 disabled:hover:bg-blue-600"
              >
                {isConnecting && <Loader2 size={14} className="animate-spin" />}
                {isConnecting ? 'Connecting...' : 'Connect'}
              </button>
            ) : (
              <>
                <button
                  onClick={handleDisconnect}
                  className="rounded-md bg-gray-800 px-4 py-2 text-sm font-medium text-gray-300 transition-colors hover:bg-gray-700"
                >
                  Disconnect
                </button>
                <button
                  onClick={handleRemoveCredentials}
                  className="rounded-md px-3 py-2 text-xs text-red-400 transition-colors hover:bg-red-500/10"
                >
                  Remove Credentials
                </button>
              </>
            )}
          </div>
        </div>

        {/* Polymarket Account Card */}
        <PolymarketAccountCard />

        {/* Sound Notifications */}
        <SoundSettingsPanel />
      </div>
    </div>
  );
}

// ── Status Badge ────────────────────────────────────────────────

function StatusBadge({ status, balance }: { status: string; balance: number | null }) {
  const dot =
    status === 'connected' ? 'bg-green-500' :
    status === 'connecting' ? 'bg-yellow-500 animate-pulse' :
    status === 'error' ? 'bg-red-500' :
    'bg-gray-600';

  const label =
    status === 'connected' ? 'Connected' :
    status === 'connecting' ? 'Connecting...' :
    status === 'error' ? 'Error' :
    'Disconnected';

  return (
    <div className="flex items-center gap-3 text-xs">
      {status === 'connected' && balance !== null && (
        <span className="text-gray-400">
          Balance: <span className="font-medium text-gray-200">${(balance / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
        </span>
      )}
      <div className="flex items-center gap-1.5">
        <span className={`inline-block h-2 w-2 rounded-full ${dot}`} />
        <span className="text-gray-400">{label}</span>
      </div>
    </div>
  );
}

// ── Key Row ─────────────────────────────────────────────────────

function KeyRow({
  keyData,
  health,
  canRemove,
  disabled,
  onRemove,
}: {
  keyData: KalshiKey;
  health?: { wsConnected: boolean; lastError: string | null };
  canRemove: boolean;
  disabled: boolean;
  onRemove: () => void;
}) {
  const truncated =
    keyData.accessKey.length > 10
      ? `${keyData.accessKey.slice(0, 6)}...${keyData.accessKey.slice(-4)}`
      : keyData.accessKey;

  return (
    <div className="flex items-center justify-between rounded-md bg-gray-800/50 px-3 py-2">
      <div className="flex items-center gap-3">
        <span className="text-xs font-medium text-gray-300">{keyData.label}</span>
        <span className="font-mono text-xs text-gray-500">{truncated}</span>
        {health && (
          <div className="flex items-center gap-1">
            <span className={`inline-block h-1.5 w-1.5 rounded-full ${health.wsConnected ? 'bg-green-500' : 'bg-gray-600'}`} />
            <span className="text-xs text-gray-500">{health.wsConnected ? 'WS live' : 'WS off'}</span>
          </div>
        )}
      </div>
      {canRemove && !disabled && (
        <button
          onClick={onRemove}
          className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-700 hover:text-gray-300"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}

// ── Add Key Form ────────────────────────────────────────────────

function AddKeyForm({
  onAdd,
  onCancel,
}: {
  onAdd: (key: KalshiKey) => void;
  onCancel: () => void;
}) {
  const [label, setLabel] = useState('');
  const [accessKey, setAccessKey] = useState('');
  const [privateKeyPem, setPrivateKeyPem] = useState('');
  const [showPem, setShowPem] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = () => {
    setLocalError(null);

    if (!accessKey.trim()) {
      setLocalError('Access key is required.');
      return;
    }
    if (!privateKeyPem.trim()) {
      setLocalError('Private key PEM is required.');
      return;
    }
    if (!privateKeyPem.includes('BEGIN PRIVATE KEY') && !privateKeyPem.includes('BEGIN RSA PRIVATE KEY')) {
      setLocalError('PEM must start with -----BEGIN PRIVATE KEY----- or -----BEGIN RSA PRIVATE KEY-----');
      return;
    }

    onAdd({
      id: crypto.randomUUID(),
      accessKey: accessKey.trim(),
      privateKeyPem: privateKeyPem.trim(),
      label: label.trim() || `Key ${Date.now().toString(36)}`,
      addedAt: Date.now(),
    });

    setLabel('');
    setAccessKey('');
    setPrivateKeyPem('');
  };

  return (
    <div className="rounded-md border border-gray-700 bg-gray-800/30 p-3">
      <div className="space-y-3">
        {/* Label */}
        <div>
          <label className="mb-1 block text-xs text-gray-400">Label (optional)</label>
          <input
            type="text"
            value={label}
            onChange={(e) => { setLabel(e.target.value); setLocalError(null); }}
            placeholder="e.g., Trading Key 1"
            className="w-full rounded-md border border-gray-700 bg-gray-900 px-3 py-1.5 text-xs text-gray-200 placeholder-gray-600 focus:border-blue-500 focus:outline-none"
          />
        </div>

        {/* Access Key */}
        <div>
          <label className="mb-1 block text-xs text-gray-400">Access Key</label>
          <input
            type="password"
            value={accessKey}
            onChange={(e) => { setAccessKey(e.target.value); setLocalError(null); }}
            placeholder="Your Kalshi access key ID"
            className="w-full rounded-md border border-gray-700 bg-gray-900 px-3 py-1.5 font-mono text-xs text-gray-200 placeholder-gray-600 focus:border-blue-500 focus:outline-none"
          />
        </div>

        {/* Private Key PEM */}
        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="text-xs text-gray-400">Private Key (PKCS#8 PEM)</label>
            <button
              onClick={() => setShowPem((v) => !v)}
              className="text-gray-500 hover:text-gray-300"
            >
              {showPem ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
          <textarea
            value={privateKeyPem}
            onChange={(e) => { setPrivateKeyPem(e.target.value); setLocalError(null); }}
            placeholder="-----BEGIN PRIVATE KEY-----"
            rows={4}
            className="w-full resize-y rounded-md border border-gray-700 bg-gray-900 px-3 py-1.5 font-mono text-xs text-gray-200 placeholder-gray-600 focus:border-blue-500 focus:outline-none"
            style={!showPem && privateKeyPem ? { WebkitTextSecurity: 'disc' } as React.CSSProperties : undefined}
          />
        </div>

        {/* Error */}
        {localError && (
          <div className="text-xs text-red-400">{localError}</div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleSubmit}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-500"
          >
            Add Key
          </button>
          <button
            onClick={onCancel}
            className="rounded-md px-3 py-1.5 text-xs text-gray-400 transition-colors hover:text-gray-300"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Polymarket Account Card ────────────────────────────────────

function PolymarketAccountCard() {
  const polyStatus = usePolyAccountStore((s) => s.status);
  const polyError = usePolyAccountStore((s) => s.error);
  const polyConfig = usePolyAccountStore((s) => s.config);
  const polyConnect = usePolyAccountStore((s) => s.connect);
  const polyDisconnect = usePolyAccountStore((s) => s.disconnect);
  const polyRemoveCredentials = usePolyAccountStore((s) => s.removeCredentials);

  const [privateKey, setPrivateKey] = useState('');
  const [signatureType, setSignatureType] = useState<0 | 1 | 2>(0);
  const [funder, setFunder] = useState('');
  const [showKey, setShowKey] = useState(false);

  const isConnected = polyStatus === 'connected';
  const isConnecting = polyStatus === 'connecting';

  const handleConnect = useCallback(async () => {
    if (!privateKey.trim()) return;
    await polyConnect({
      privateKey: privateKey.trim(),
      signatureType,
      funder: funder.trim() || undefined,
    });
  }, [privateKey, signatureType, funder, polyConnect]);

  const handleDisconnect = useCallback(() => {
    polyDisconnect();
    setPrivateKey('');
    setFunder('');
  }, [polyDisconnect]);

  const handleRemoveCredentials = useCallback(() => {
    polyRemoveCredentials();
    setPrivateKey('');
    setFunder('');
  }, [polyRemoveCredentials]);

  return (
    <div className="rounded-lg border border-gray-800 bg-gray-900/50 p-5">
      {/* Card Header */}
      <div className="mb-5 flex items-center justify-between">
        <h2 className="text-base font-medium">Polymarket Account</h2>
        <StatusBadge status={polyStatus} balance={null} />
      </div>

      {!isConnected && (
        <>
          {/* Private Key */}
          <div className="mb-4">
            <div className="mb-1 flex items-center justify-between">
              <label className="text-xs font-medium text-gray-400">Private Key</label>
              <button
                onClick={() => setShowKey((v) => !v)}
                className="text-gray-500 hover:text-gray-300"
              >
                {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <input
              type={showKey ? 'text' : 'password'}
              value={privateKey}
              onChange={(e) => setPrivateKey(e.target.value)}
              placeholder="0x..."
              className="w-full rounded-md border border-gray-700 bg-gray-900 px-3 py-1.5 font-mono text-xs text-gray-200 placeholder-gray-600 focus:border-blue-500 focus:outline-none"
            />
          </div>

          {/* Signature Type */}
          <div className="mb-4">
            <label className="mb-1.5 block text-xs font-medium text-gray-400">Signature Type</label>
            <div className="flex gap-2">
              {([0, 1, 2] as const).map((t) => {
                const label = t === 0 ? 'EOA' : t === 1 ? 'Magic' : 'Gnosis';
                return (
                  <button
                    key={t}
                    onClick={() => setSignatureType(t)}
                    className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                      signatureType === t
                        ? 'bg-blue-500/20 text-blue-400 ring-1 ring-blue-500/40'
                        : 'bg-gray-800 text-gray-400 hover:text-gray-300'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Funder (shown for Magic/Gnosis) */}
          {signatureType !== 0 && (
            <div className="mb-4">
              <label className="mb-1 block text-xs font-medium text-gray-400">Funder Address (optional)</label>
              <input
                type="text"
                value={funder}
                onChange={(e) => setFunder(e.target.value)}
                placeholder="0x..."
                className="w-full rounded-md border border-gray-700 bg-gray-900 px-3 py-1.5 font-mono text-xs text-gray-200 placeholder-gray-600 focus:border-blue-500 focus:outline-none"
              />
            </div>
          )}
        </>
      )}

      {/* Connected state — show truncated key */}
      {isConnected && polyConfig && (
        <div className="mb-4">
          <label className="mb-1 block text-xs font-medium text-gray-400">Connected Wallet</label>
          <div className="rounded-md bg-gray-800/50 px-3 py-2 font-mono text-xs text-gray-400">
            {polyConfig.privateKey.slice(0, 6)}...{polyConfig.privateKey.slice(-4)}
          </div>
        </div>
      )}

      {/* Error Display */}
      {polyError && (
        <div className="mt-4 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">
          {polyError}
        </div>
      )}

      {/* Action Buttons */}
      <div className="mt-5 flex items-center gap-3 border-t border-gray-800 pt-4">
        {!isConnected ? (
          <button
            onClick={handleConnect}
            disabled={!privateKey.trim() || isConnecting}
            className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-500 disabled:opacity-50 disabled:hover:bg-blue-600"
          >
            {isConnecting && <Loader2 size={14} className="animate-spin" />}
            {isConnecting ? 'Connecting...' : 'Connect'}
          </button>
        ) : (
          <>
            <button
              onClick={handleDisconnect}
              className="rounded-md bg-gray-800 px-4 py-2 text-sm font-medium text-gray-300 transition-colors hover:bg-gray-700"
            >
              Disconnect
            </button>
            <button
              onClick={handleRemoveCredentials}
              className="rounded-md px-3 py-2 text-xs text-red-400 transition-colors hover:bg-red-500/10"
            >
              Remove Credentials
            </button>
          </>
        )}
      </div>
    </div>
  );
}
